/**
 * Grid of parts added to an Expense Inventory case's Sampling tab. Columns:
 * Part, Base or Capital (no header), Lot/Serial, Qty, Actual Qty, Source
 * Location, Expiration Date, and Action — matching the Parts tab used on
 * Orders Usage Details. Parts added from the Catalog get a catalog icon, and
 * are red while their lot is not in inventory, and get an editable Lot/Serial
 * field that suggests the lots of that part in inventory and still takes a
 * typed lot, matching the catalog lot field on Bill Only. For Ops users, two
 * additional columns appear before Action: Cost, and
 * a per-line Approve/Reject decision (reusing InventoryApproveRejectDataCell,
 * restricted to those two options). The Decision column header shows
 * Approve All/Reject All buttons that bulk-set every line's decision,
 * matching the Inventory Request pattern.
 * @module src/components/Inventory/expense/detail/ExpenseInventorySampling/ExpenseInventorySamplingTable
 */
import React, { FC } from 'react';
import Input from '@salesforce/design-system-react/components/input';
import Button from '@salesforce/design-system-react/components/button';
import Spinner from '@salesforce/design-system-react/components/spinner';
import { format, isValid } from 'date-fns';
import InventoryApproveRejectDataCell from '../../../InventoryQueue/DataTableCells/InventoryApproveRejectDataCell';
import InputWithAutoSuggestion from '../../../../Usage/UsageDetails/UsageTabs/Parts/InputWithAutoSuggestion';
import {
  EXPENSE_INVENTORY_WORKFLOW_ACTION_OPTIONS,
  MAX_QUANTITY,
  WORKFLOW_ACTION,
} from '../../../../../util/constants';
import {
  getFormattedBaseCapital,
  getSvgIcon,
  getUSDString,
} from '../../../../../util/utilityFunctions';
import { CatalogLotOption, isSamplingLineRed, toCatalogLotInput } from './samplingLineRules';
import {
  ALL_SAMPLING_COLUMNS_VISIBLE,
  SamplingColumnVisibility,
  getSamplingGridTemplateColumns,
} from './samplingColumnConfig';
import styles from './styles.module.scss';

export interface SamplingLineItem {
  uniqueId: string;
  sfid?: string;
  externalId?: string;
  productId?: string;
  productNumber?: string;
  productDescription?: string;
  baseorCapital?: string;
  lotNumber?: string;
  originalLotNumber?: string;
  quantity?: number | string;
  originalQuantity?: number | string;
  actualQuantity?: number | string;
  sourceLocation?: string;
  subInventoryCode?: string;
  expirationDate?: string;
  maxQuantity?: number;
  iscatalog?: boolean;
  isAddedFromCatalog?: boolean;
  isValidLotSerialCombination?: boolean;
  cost?: number | string;
  workflowAction?: string;
  userworkflowAction?: { label: string; value: string }[];
}

interface Props {
  items: SamplingLineItem[];
  viewOnly?: boolean;
  isOpsUser?: boolean;
  isReview?: boolean;
  columnVisibility?: SamplingColumnVisibility;
  deletingUniqueId?: string | null;
  savingDecisionUniqueIds?: string[];
  disableBulkDecisionActions?: boolean;
  onQuantityChange: (uniqueId: string, quantity: number) => void;
  onLotNumberChange: (uniqueId: string, lotNumber: string) => void;
  onLotNumberBlur: (uniqueId: string) => void;
  onLotNumberSelect: (uniqueId: string, lotNumber: string) => void;
  catalogLotOptionsByProductNumber?: Record<string, CatalogLotOption[]>;
  onDelete: (uniqueId: string) => void;
  onDecisionChange: (uniqueId: string, decision: { label: string; value: string }[]) => void;
  onApproveAllDecisions: () => void;
  onRejectAllDecisions: () => void;
}

const catalogIcon = getSvgIcon('catalog');

const formatExpirationDate = (date?: string): string => {
  if (!date) {
    return '--';
  }
  const parsedDate = new Date(date);
  if (!isValid(parsedDate)) {
    return '--';
  }
  return format(parsedDate, 'MM/dd/yyyy');
};

const ExpenseInventorySamplingTable: FC<Props> = ({
  items,
  viewOnly = false,
  isOpsUser = false,
  isReview = false,
  columnVisibility = ALL_SAMPLING_COLUMNS_VISIBLE,
  deletingUniqueId = null,
  savingDecisionUniqueIds = [],
  disableBulkDecisionActions = false,
  onQuantityChange,
  onLotNumberChange,
  onLotNumberBlur,
  onLotNumberSelect,
  catalogLotOptionsByProductNumber = {},
  onDelete,
  onDecisionChange,
  onApproveAllDecisions,
  onRejectAllDecisions,
}) => {
  const gridTemplateColumns = getSamplingGridTemplateColumns(
    columnVisibility,
    isOpsUser && isReview
  );

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader} style={{ gridTemplateColumns }}>
        {columnVisibility.part && <span className={styles.partCell}>Part</span>}
        {columnVisibility.part && <span />}
        {columnVisibility.lotSerial && <span>Lot/Serial</span>}
        {columnVisibility.qty && <span>Qty</span>}
        {columnVisibility.actualQty && <span>Actual Qty</span>}
        {columnVisibility.sourceLocation && <span>Source Location</span>}
        {columnVisibility.expiration && <span>Expiration Date</span>}
        {isOpsUser && isReview && <span>Cost</span>}
        {isOpsUser && isReview && (
          <span className={styles.decisionHeaderActions}>
            <Button
              style={{ color: 'black', fontWeight: 600, fontSize: '13px' }}
              label="Approve All"
              disabled={viewOnly || disableBulkDecisionActions}
              onClick={onApproveAllDecisions}
            />
            <Button
              style={{ color: 'black', fontWeight: 600, fontSize: '13px' }}
              label="Reject All"
              disabled={viewOnly || disableBulkDecisionActions}
              onClick={onRejectAllDecisions}
            />
          </span>
        )}
        <span>Action</span>
      </div>
      {items.length === 0 && <div className={styles.emptyState}>No parts added yet.</div>}
      {items.map(item => (
        <div className={styles.tableRow} style={{ gridTemplateColumns }} key={item.uniqueId}>
          {columnVisibility.part && (
            <span
              className={`${styles.partCell} ${isSamplingLineRed(item) ? styles.catalogPart : ''}`}
            >
              {item.productNumber || ''}
              {item.productDescription ? ` - ${item.productDescription}` : ''}
              {item.isAddedFromCatalog && (
                <img
                  src={catalogIcon}
                  className={styles.catalogIcon}
                  alt="catalog part"
                  title="This part was selected from the Catalog"
                />
              )}
            </span>
          )}
          {columnVisibility.part && (
            <span>{getFormattedBaseCapital(item.baseorCapital || '')}</span>
          )}
          {columnVisibility.lotSerial &&
            (item.iscatalog ? (
              // The shared field keeps what is typed to itself; its change and blur reach this cell,
              // so the row keeps the typed lot and the table's settle timer sees each keystroke.
              // The field resets its text only when the lot becomes empty, so it is remounted when the
              // saved lot changes, and it lists no lots while the table is disabled: its wrapper still
              // takes focus and opens the list, and a pick queued behind a save could land on a line
              // that save has just turned into an inventory line.
              <span
                id={`sampling-lot-${item.uniqueId}`}
                className={styles.lotNumberInput}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                  onLotNumberChange(item.uniqueId, toCatalogLotInput(event.target.value))
                }
                onBlur={(): void => onLotNumberBlur(item.uniqueId)}
              >
                <InputWithAutoSuggestion
                  key={`${item.uniqueId}-${item.originalLotNumber || ''}`}
                  disabled={viewOnly}
                  lineItem={{
                    ...item,
                    lotOptions: viewOnly
                      ? []
                      : catalogLotOptionsByProductNumber[item.productNumber || ''] || [],
                  }}
                  handleSuggestionSelect={(suggestion: { label?: string }): void =>
                    onLotNumberSelect(item.uniqueId, suggestion?.label || '')
                  }
                  forceUppercase
                />
              </span>
            ) : (
              <span>{item.lotNumber || '--'}</span>
            ))}
          {columnVisibility.qty && (
            <span>
              <Input
                disabled={viewOnly}
                id={`sampling-qty-${item.uniqueId}`}
                minValue={1}
                maxValue={item.maxQuantity || MAX_QUANTITY}
                value={item.quantity}
                variant="counter"
                className={styles.quantityInput}
                onChange={(_event: unknown, data: { number: number }): void =>
                  onQuantityChange(item.uniqueId, data.number)
                }
              />
            </span>
          )}
          {columnVisibility.actualQty && <span>{item.actualQuantity ?? item.quantity ?? 0}</span>}
          {columnVisibility.sourceLocation && <span>Repstock</span>}
          {columnVisibility.expiration && <span>{formatExpirationDate(item.expirationDate)}</span>}
          {isOpsUser && isReview && <span>{getUSDString(String(item.cost || 0))}</span>}
          {isOpsUser && isReview && (
            <span>
              {savingDecisionUniqueIds.includes(item.uniqueId) ? (
                <Spinner size="x-small" variant="base" assistiveText={{ label: 'Saving...' }} />
              ) : (
                <InventoryApproveRejectDataCell
                  key={`${item.uniqueId}-${(item.userworkflowAction || [])
                    .map(decision => decision.value)
                    .join(',')}`}
                  item={{
                    workflowAction: item.workflowAction || WORKFLOW_ACTION.NO_ACTION,
                    userworkflowAction: item.userworkflowAction as any,
                  }}
                  viewOnly={viewOnly}
                  options={EXPENSE_INVENTORY_WORKFLOW_ACTION_OPTIONS}
                  handleUserWorkflowAction={(updated: {
                    userworkflowAction: { label: string; value: string }[];
                  }): void => onDecisionChange(item.uniqueId, updated.userworkflowAction)}
                />
              )}
            </span>
          )}
          <span>
            {item.uniqueId === deletingUniqueId ? (
              <Spinner size="x-small" variant="base" assistiveText={{ label: 'Deleting...' }} />
            ) : (
              <Button
                disabled={viewOnly}
                iconCategory="utility"
                iconName="delete"
                iconSize="large"
                iconVariant="bare"
                variant="icon"
                onClick={(): void => onDelete(item.uniqueId)}
              />
            )}
          </span>
        </div>
      ))}
    </div>
  );
};

export default ExpenseInventorySamplingTable;
